import { capitalize } from 'lodash-es';

import { IFilterConfigItem } from '@/bundles/GroupFilters/interfaces/IFilterConfigItem';
import { FilterCategoryEnum, FilterTypeEnum } from '@/bundles/GroupFilters/enums/FilterTypeEnum';
import { IFilterButtonGroupItems } from '@/bundles/GroupFilters/interfaces/inputs/IFilterButtonGroupItems';
import { FilterBusinessLineAll, FilterBusinessLineItems } from '@/bundles/GroupFilters/enums/FilterBusinessLineEnum';
import { getPropertyTypesWithMixedUseForFilters } from '@/bundles/GroupFilters/consts';
import { getSelectItems } from '@/bundles/Pipelines/helpers';
import { Nullable } from '@/utils/types';
import { formatToSF } from '@/bundles/GroupFilters/helpers/formatters/formatToSF';
import { pipelineStageLabels } from '@/bundles/Pipelines/types/stageEnum';
import { ITextValue } from '@/types/common';
import { getContactRolesBySource } from '@/bundles/Contact/helpers/cards/contactsCard';
import store from '@/store/store';
import {
  FilterReferralSendTypeEnum,
  FilterReferralSendTypeItems
} from '@/bundles/GroupFilters/enums/FilterReferralSendTypeEnum';
import i18n from '@/bundles/Localization';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';
import { getShowArchiveFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getShowArchiveFilterConfig';
import { getStaticSetFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getStaticSetFilterConfig';

const getListByCategory = (category: string) => store.getters['Admin/getListByCategory'](category);

export const getPipelineFilters = (): IFilterConfigItem[] => {
  const propertyTypesWithMixedUse = getPropertyTypesWithMixedUseForFilters();

  return [
    // FilterCategoryEnum.pipeline_details start
    {
      title: i18n.t(LocalizationEnum.business_line) as string,
      name: 'pipeline_business_line',
      type: FilterTypeEnum.button_group,
      items: <IFilterButtonGroupItems>FilterBusinessLineItems,
      defaultValue: FilterBusinessLineAll,
      category: FilterCategoryEnum.pipeline_details,
    },
    {
      title: 'Property Type',
      name: 'property_type',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: propertyTypesWithMixedUse,
      category: FilterCategoryEnum.pipeline_details,
    },
    {
      title: 'Transaction Category',
      name: 'pipeline_category',
      type: FilterTypeEnum.transaction_category_checkbox_group,
      category: FilterCategoryEnum.pipeline_details,
    },
    {
      title: 'Pipeline Size',
      category: FilterCategoryEnum.pipeline_details,
      type: FilterTypeEnum.pipeline_size_group,
      children: [
        {
          title: 'Space Size',
          name: 'pipeline_size',
          type: FilterTypeEnum.number_range,
          formatter: (v: Nullable<number>) => formatToSF(v),
        },
        {
          title: 'Land Size',
          name: 'pipeline_land_size',
          type: FilterTypeEnum.land_size_range, // sending with pipeline_land_size_unit
        },
        {
          title: 'Units / Keys',
          name: 'pipeline_units',
          type: FilterTypeEnum.number_range,
        },
      ]
    },
    {
      title: 'Estimated Close Date',
      name: 'pipeline_close_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.pipeline_details,
    },
    {
      title: 'Total Consideration',
      name: 'pipeline_consideration',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.pipeline_details,
    },
    {
      title: 'In-House Gross',
      name: 'pipeline_inhouse_gross',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.pipeline_details,
    },
    {
      title: `${capitalize(i18n.tc(LocalizationEnum.broker))} Gross`,
      name: 'pipeline_broker_gross',
      type: FilterTypeEnum.currency_range,
      category: FilterCategoryEnum.pipeline_details,
    },
    {
      title: 'Commission %',
      name: 'pipeline_commission_percentage',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.pipeline_details,
    },
    {
      title: 'Source Category',
      name: 'pipeline_source',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getSelectItems().sources,
      category: FilterCategoryEnum.pipeline_details,
    },
    {
      title: 'Pipeline Stage',
      name: 'pipeline_stage',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: Object.entries(pipelineStageLabels).map<ITextValue<number>>((
        [value, text]) => ({ text, value: Number(value) }
      )),
      category: FilterCategoryEnum.pipeline_details,
    },
    {
      title: capitalize(i18n.tc(LocalizationEnum.broker)),
      name: 'pipeline_broker',
      type: FilterTypeEnum.brokers_select,
      category: FilterCategoryEnum.pipeline_details,
    },
    {
      title: 'Contact Name',
      name: 'contact_names',
      type: FilterTypeEnum.contact_names_multiple_select,
      category: FilterCategoryEnum.pipeline_details,
    },
    {
      title: 'Pipeline Contact Role',
      name: 'pipeline_contact_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: [...getContactRolesBySource('pipeline'), ...getContactRolesBySource('pipeline_primary_client')],
      category: FilterCategoryEnum.pipeline_details,
    },
    // FilterCategoryEnum.pipeline_details end
    // FilterCategoryEnum.referral_filters start
    {
      title: 'Referral Send Type',
      name: 'pipeline_inbound_outbound',
      type: FilterTypeEnum.button_group,
      items: <IFilterButtonGroupItems>FilterReferralSendTypeItems,
      defaultValue: FilterReferralSendTypeEnum.all,
      category: FilterCategoryEnum.referral_filters,
    },
    {
      title: 'Referral Contact Keyword',
      name: 'pipeline_referral_keyword',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.referral_filters,
    },
    // FilterCategoryEnum.referral_filters end
    // FilterCategoryEnum.created_edited start
    {
      title: 'Created/Updated Date',
      name: 'pipeline_view_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created Date',
      name: 'pipeline_created_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited Date',
      name: 'pipeline_edited_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created/Edited by',
      name: 'pipeline_view',
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created by',
      name: 'pipeline_created_by',
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited by',
      name: 'pipeline_edited_by',
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.created_edited,
    },
    // FilterCategoryEnum.created_edited end
    getShowArchiveFilterConfig(),
    getStaticSetFilterConfig('pipeline'),
  ].filter(Boolean) as IFilterConfigItem[];
};
